import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";

import type { CardGridComponentProps } from "../types";

import { Skeleton } from "@/components/ui/v2/skeleton";

function CardGridComponent({
  cardTemplate = [],
  columns = 3,
  itemKey,
  items,
  isLoading,
  infiniteScroll,
  onItemClick,
  isItemClickable,
  renderWithContext,
}: CardGridComponentProps) {
  const { t } = useTranslation();

  const columnClass =
    columns === 2
      ? "xl:grid-cols-2"
      : columns === 4
        ? "xl:grid-cols-4"
        : "xl:grid-cols-3";

  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 ${columnClass} gap-4`}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-lg" />
        ))}
      </div>
    );
  }

  if (!items?.length) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed p-12 text-muted-foreground">
        {t("common.noResults", { defaultValue: "No items found" })}
      </div>
    );
  }

  const showSkeletons = infiniteScroll?.isFetchingMore;

  const cardItems = items.map((item, index) => {
    const record = item as Record<string, unknown>;
    const key = itemKey && record[itemKey] != null ? String(record[itemKey]) : index;
    const clickable = !!onItemClick && (!isItemClickable || isItemClickable(item));

    return (
      <div
        key={key}
        data-slot="panel"
        className={`rounded-lg border bg-card p-5 shadow-sm flex flex-col gap-3 ${clickable ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`}
        onClick={clickable ? () => onItemClick?.(item) : undefined}
      >
        {cardTemplate.map((templateComponent, templateIndex) => {
          const rendered = renderWithContext(templateComponent, { data: record }) as ReactNode;
          return rendered ? (
            <div key={templateIndex}>
              {rendered}
            </div>
          ) : null;
        })}
      </div>
    );
  });

  const skeletonItems = (() => {
    if (!showSkeletons) return null;
    const remainder = items.length % columns;
    const count = (remainder === 0 ? 0 : columns - remainder) + columns;
    return Array.from({ length: count }).map((_, i) => (
      <div key={`skeleton-${i}`} data-slot="panel" className="rounded-lg border bg-card p-5 shadow-sm flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1.5 flex-1">
            <Skeleton className="h-5 w-3/4 rounded" />
            <Skeleton className="h-5 w-1/2 rounded" />
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Skeleton className="h-5 w-14 rounded-full" />
            <Skeleton className="h-5 w-14 rounded-full" />
          </div>
        </div>
        <Skeleton className="h-5 w-2/5 rounded" />
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, j) => (
            <div key={j} className="flex items-center justify-between gap-4">
              <Skeleton className="h-5 w-1/3 rounded" />
              <Skeleton className="h-5 w-1/4 rounded" />
            </div>
          ))}
          <div className="flex items-center justify-between gap-4">
            <Skeleton className="h-5 w-1/3 rounded" />
            <Skeleton className="h-5 w-20 rounded" />
          </div>
        </div>
      </div>
    ));
  })();

  const grid = (
    <div className={`grid grid-cols-1 md:grid-cols-2 ${columnClass} gap-4`}>
      {cardItems}
      {skeletonItems}
    </div>
  );

  if (infiniteScroll) {
    return (
      <InfiniteScroll
        dataLength={items.length}
        next={infiniteScroll.fetchMore}
        hasMore={infiniteScroll.hasMore}
        loader={null}
        scrollThreshold={0.9}
        scrollableTarget="main-content-container"
      >
        {grid}
      </InfiniteScroll>
    );
  }

  return grid;
}

export { CardGridComponent };
